import styled from 'styled-components'

import { Flex, RightPanel, Text, Icon, Table, Button, IconButton, Badge } from 'components'
import { TextProps } from 'components/Text'
import i18next from 'i18next'
import { mapDuplicateInfoField } from 'queries/lead-duplicate'
import { LeadDuplicateDetails } from 'services/types/lead'
import { formatDatetime } from 'utils/datetime'
import { capitalizeFirstLetter } from 'utils/string'
import { Z_INDEX } from 'utils/constants'
import useUserPermissions from 'hooks/use-user-permissions'
import useAppSetting from 'hooks/use-app-setting'

type Props = {
  data: LeadDuplicateDetails
  onClose?: () => void
  handleClickLink?: () => void
  onMerge?: () => void
  hideMergeButton?: boolean
}

const DuplicateInfo = ({ data, onClose, handleClickLink, onMerge, hideMergeButton = false }: Props) => {
  const {
    key: { DISABLE_LEAD_DUPLICATE_CHECKER },
  } = useAppSetting()
  const [permissions] = useUserPermissions('accountWorkflow')

  const scores = data.scores.map((i) => ({
    attribute: capitalizeFirstLetter(mapDuplicateInfoField(i.field)),
    percentage: `${i.score}%`,
  }))

  const canMerge = permissions.canMerge && permissions.canEditAccount && data.has_access
  const isLostOrInvalid = ['lost', 'invalid'].includes(data.lead_status_name.toLowerCase())
  const isUnassigned = !data.lead_owner_name && !data.lead_owner_team_name

  return (
    <RightPanel
      title={
        <Flex gap="1rem" justifyContent="space-between" alignItems="center">
          <Text>duplicate_info_lbl</Text>
          <IconButton variant="blandGhost" icon={['fas', 'fa-xmark']} onClick={onClose} />
        </Flex>
      }
      width={540}
      leftButtonComponent={<div></div>}
      rightButtonComponent={
        canMerge && !hideMergeButton ? (
          !isLostOrInvalid || permissions.canEditLostInvalidLeads ? (
            <Button variant="outline" icon={['fas', 'fa-merge']} onClick={onMerge} textTransform="capitalize">
              merge_lead_lbl
            </Button>
          ) : (
            <></>
          )
        ) : (
          <></>
        )
      }
      zIndex={Z_INDEX.window + 2}
      hideBackdrop
    >
      <InfoGrid>
        <Label>lead_lbl</Label>
        <Flex gap="0.5rem">
          {data.has_access ? (
            <>
              <Value variant="link" onClick={handleClickLink} textDecoration="none" fontWeight="medium">
                {data.lead_name}
              </Value>
              <Icon icon={['far', 'fa-arrow-up-right-from-square']} onClick={handleClickLink} />
            </>
          ) : (
            <Value fontWeight="medium">{data.lead_name}</Value>
          )}
        </Flex>

        <Label>lead_id_lbl</Label>
        <Value>{data.lead_id}</Value>

        <Label>lead_type_lbl</Label>
        <Value>{data.lead_type_name}</Value>

        <Label isCapitalizedFirstLetter>{i18next.t('lead_status_lbl').toLocaleLowerCase()}</Label>
        <Value>
          <Badge text={data.lead_status_name} textColor="grayShadeBlack2" />
        </Value>

        <Label>lead_owner_lbl</Label>
        <Value color={isUnassigned ? 'red' : undefined}>
          {data.lead_owner_name || data.lead_owner_team_name || 'unassigned_lbl'}
        </Value>

        <Label>lead_in_lbl</Label>
        <Value>{formatDatetime(data.lead_in_date)}</Value>

        <Label>last_activity_lbl</Label>
        <Value>{formatDatetime(data.last_activity.last_activity_ts) || '-'}</Value>
      </InfoGrid>
      <Separator />

      <Text variant="h6" mb="1rem">
        {`${i18next.t('duplicates_lbl')} - ${
          data.duplicate_type === 'exact' ? i18next.t('duplicate_type_exacts') : i18next.t('duplicate_type_potentials')
        }`}
      </Text>
      <Table
        uniqueTableName="duplicate-info-scores"
        columns={[
          {
            Header: 'attribute_lbl',
            accessor: 'attribute',
          },
          {
            Header: 'percentage',
            accessor: 'percentage',
          },
        ]}
        data={scores}
        disablePagination
      />
    </RightPanel>
  )
}

const Separator = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.gray3};
  margin: 1rem 0;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1rem;
`

const Label = (props: TextProps) => (
  <Flex alignItems="center">
    <Text color="grayShadeBlack2" {...props} />
  </Flex>
)
const Value = (props: TextProps) => <Text variant="h6" {...props} />

export default DuplicateInfo
